import { Bound } from "lib/@components/binding/Bound"
import { isEqual } from "lib/isEqual"
import { Box, IconButton, Tooltip } from "@mui/material"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { RenderSelector } from "event-definitions"
import { DarkThemeProvider } from "theme/darkThemeProvider"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import noop from "lib/noop"
import Iconify from "minimals-template/components/Iconify"
import { RegimeDraftLockTooltip } from "../regime-draft-locking/regime-draft-lock-tooltip"

export function Different({ field, comparedTo, children, onRevert = noop }) {
    const { target } = useBoundContext()
    RenderSelector.useRefresh(noChange)
    const refresh = useRefresh()
    const isDifferent = Object.get(target, field) && !isEqual(Object.get(target, field), Object.get(comparedTo, field))
    const title = (
        <Bound editMode={false} target={comparedTo}>
            {children}
        </Bound>
    )
    return isDifferent ? (
        <Tooltip
            arrow
            placement="top-start"
            title={
                isDifferent && (
                    <DarkThemeProvider>
                        <Bound mustShowContent={true}>
                            <Box display="flex" alignItems="center">
                                <Box mr={1}>{title}</Box>
                                <RegimeDraftLockTooltip>
                                    <IconButton
                                        aria-label="Revert change"
                                        size="small"
                                        color="secondary"
                                        onClick={revert}
                                        data-cy="UndoButton"
                                    >
                                        <Iconify icon="mdi:undo" />
                                    </IconButton>
                                </RegimeDraftLockTooltip>
                            </Box>
                        </Bound>
                    </DarkThemeProvider>
                )
            }
        >
            <Box display="flex" alignItems="center">
                <Box color="warning.main" sx={{ zoom: 0.5 }} fontSize={16} width={16} pl={1}>
                    <Iconify icon="mdi:circle" />
                </Box>

                {children}
            </Box>
        </Tooltip>
    ) : (
        <>
            <Box width={8} />
            {children}
        </>
    )

    function revert() {
        // Reverting means "no override", so the key is REMOVED and the task inherits from the
        // schedule again. Writing a copy of the inherited value, which this did until now, leaves a
        // pin that happens to match today and stops matching the moment the schedule changes. That
        // is the exact defect the timing repair exists to undo, so reverting must not create one.
        // The old empty-string fallback here is also where the empty-string overrides in live data
        // came from: a field with no inherited value stored an empty string, which the platform then
        // read as zero.
        deleteField(target, field)
        onRevert()
        refresh()
    }
}

// Removes a field at a dotted or flat path. Every current caller passes a flat field name, but the
// dotted form is what the accessors either side of this accept, so it accepts the same. It walks the
// path itself rather than using a helper, so it depends on nothing and is directly testable.
function deleteField(object, path) {
    const parts = `${path}`.split(".")
    const key = parts.pop()
    let parent = object
    for (const part of parts) {
        if (!parent || typeof parent !== "object") return
        parent = parent[part]
    }
    if (parent && typeof parent === "object") delete parent[key]
}
